import { useCallback, useEffect, useState } from "react";
import type { Business } from "@/types/directory";
import { queryBusinesses } from "@/services/directoryService";
import BusinessGrid from "@/components/feature/BusinessGrid";

interface EmergencyListingsProps {
  category: string;
  keyword?: string;
  district?: string;
  pageSize?: number;
}

export default function EmergencyListings({
  category,
  keyword = "",
  district = "",
  pageSize = 6,
}: EmergencyListingsProps) {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const load = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    queryBusinesses({ category, keyword, district, sort: "rating", page: 1, pageSize })
      .then((result) => {
        if (active) setBusinesses(result.items);
      })
      .catch(() => {
        if (active) {
          setBusinesses([]);
          setError("We couldn't load these listings right now. Please try again.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [category, keyword, district, pageSize, reloadKey]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-red-200 bg-red-50/60 px-6 py-14 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
          <i className="ri-error-warning-line text-2xl" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
          Something went wrong
        </h3>
        <p className="mt-2 max-w-md text-sm text-foreground-600">{error}</p>
        <button
          type="button"
          onClick={load}
          className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-red-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-red-700 transition-colors hover:bg-red-50"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-refresh-line text-base" />
          </span>
          Retry
        </button>
      </div>
    );
  }

  return (
    <BusinessGrid
      businesses={businesses}
      loading={loading}
      emptyTitle="No listings found here yet"
      emptyMessage={
        district
          ? "We're still adding verified emergency and healthcare listings for this district. Try another area or call the helplines above for immediate help."
          : "We're still adding verified emergency and healthcare listings for this area. Call the helplines above for immediate help."
      }
    />
  );
}